<script setup>
import { onMounted, reactive, ref } from "vue"
import { useRoute } from "vue-router"
import Swal from "sweetalert2"
import api from "@/api/axios.js"
import { useAuth } from "@/composables/useAuth"
import EditarCitaModal from "./components/EditarCitaModal.vue"
import HistorialCitaModal from "./components/HistorialCitaModal.vue"

const { user, hasRole } = useAuth()
const route = useRoute()

const LABELS_ESTADO = {
  pendiente: "Pendiente",
  confirmada: "Confirmada",
  completada: "Completada",
  cancelada: "Cancelada"
}

const ESTADO_BADGE = {
  pendiente: "bg-warning text-dark",
  confirmada: "bg-info",
  completada: "bg-success",
  cancelada: "bg-secondary"
}

const citas = ref([])
const cargando = ref(true)
// "archivada" filtra citas completadas, canceladas o con fecha de atención ya
// vencida (aunque nunca se hayan marcado como completadas). Esas son las que
// el Calendario oculta, así que este listado es donde quedan visibles.
const filtros = reactive({ estado: "", archivada: route.query.archivada === "true" ? "true" : "" })

async function cargar() {
  cargando.value = true
  try {
    const params = {}
    if (filtros.estado) params.estado = filtros.estado
    if (filtros.archivada) params.archivada = filtros.archivada

    const { data } = await api.get("/citas", { params })
    citas.value = data.citas
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar el listado de citas",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    cargando.value = false
  }
}

onMounted(cargar)

function formatFecha(value) {
  if (!value) return ""
  return new Date(value).toLocaleDateString("es-CO", { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" })
}

function puedeConfirmarCompletar(cita) {
  return hasRole("admin") || user.value?.id === cita.asesorLiderId
}

function puedeCancelar(cita) {
  // asesor_chat puede cancelar cualquier cita activa, no solo las que creó.
  return hasRole("admin") || hasRole("asesor_chat") || user.value?.id === cita.asesorLiderId || user.value?.id === cita.creadoPor
}

function puedeEditar(cita) {
  // Cualquier persona con acceso al módulo puede editar los datos de la cita
  // mientras no esté en un estado final; el historial registra quién hizo qué cambio.
  return !["completada", "cancelada"].includes(cita.estado)
}

async function cambiarEstado(cita, estado) {
  try {
    await api.put(`/citas/${cita.id}/estado`, { estado })
    await cargar()
    Swal.fire({ icon: "success", title: "Cita actualizada", timer: 1500, showConfirmButton: false })
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo actualizar la cita",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

const citaEnEdicion = ref(null)

function abrirEdicion(cita) {
  citaEnEdicion.value = cita
}

function cerrarEdicion() {
  citaEnEdicion.value = null
}

async function alGuardarEdicion() {
  cerrarEdicion()
  await cargar()
}

const citaEnHistorial = ref(null)

function abrirHistorial(cita) {
  citaEnHistorial.value = cita
}

function cerrarHistorial() {
  citaEnHistorial.value = null
}
</script>

<template>
  <div class="container-fluid py-4">
    <div class="card shadow-sm">
      <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h5 class="mb-0"><i class="ni ni-collection text-primary me-2"></i>Mis Citas</h5>
        <div class="d-flex align-items-end gap-3 flex-wrap">
          <div>
            <label class="form-label text-sm mb-1">Estado</label>
            <select class="form-select form-select-sm" v-model="filtros.estado" @change="cargar">
              <option value="">Todas</option>
              <option value="pendiente">Pendientes</option>
              <option value="confirmada">Confirmadas</option>
              <option value="completada">Completadas</option>
              <option value="cancelada">Canceladas</option>
            </select>
          </div>
          <div>
            <label class="form-label text-sm mb-1">Vista</label>
            <select class="form-select form-select-sm" v-model="filtros.archivada" @change="cargar">
              <option value="">Todas</option>
              <option value="false">Solo activas (en el calendario)</option>
              <option value="true">Solo archivadas</option>
            </select>
          </div>
        </div>
      </div>

      <div class="card-body">
        <div v-if="cargando" class="text-center text-muted py-5">
          <div class="spinner-border spinner-border-sm me-2" role="status"></div>
          Cargando citas...
        </div>

        <div v-else class="table-responsive">
          <table class="table table-striped table-bordered align-middle table-sm">
            <thead class="table-dark">
              <tr>
                <th>Fecha</th>
                <th>Hora</th>
                <th>Cliente</th>
                <th>Celular</th>
                <th>Asesor líder</th>
                <th>Ciudad</th>
                <th>Tipo</th>
                <th>Estado</th>
                <th>Creado por</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="citas.length === 0">
                <td colspan="10" class="text-center text-muted py-4">No hay citas para este filtro.</td>
              </tr>
              <tr v-for="c in citas" :key="c.id">
                <td>{{ formatFecha(c.fecha) }}</td>
                <td>{{ c.horaInicio }} - {{ c.horaFin }}</td>
                <td>{{ c.nombreCliente }}</td>
                <td>{{ c.celular }}</td>
                <td>{{ c.asesorLiderNombre }}</td>
                <td>{{ c.ciudadNombre || "—" }}</td>
                <td>
                  <span class="badge" :class="c.tipo === 'virtual' ? 'bg-primary' : 'bg-light text-dark'">
                    {{ c.tipo === "virtual" ? "Virtual" : "Presencial" }}
                  </span>
                  <a
                    v-if="c.tipo === 'virtual' && c.linkReunion"
                    :href="c.linkReunion" target="_blank" rel="noopener"
                    class="ms-1" title="Abrir Google Meet"
                  >
                    <i class="bi bi-camera-video text-primary"></i>
                  </a>
                </td>
                <td>
                  <span class="badge" :class="ESTADO_BADGE[c.estado]">{{ LABELS_ESTADO[c.estado] }}</span>
                  <span
                    v-if="c.archivada && !['completada', 'cancelada'].includes(c.estado)"
                    class="badge bg-light text-dark border ms-1"
                    title="Ya pasó la fecha de atención y sigue sin marcarse como completada o cancelada"
                  >
                    Vencida
                  </span>
                </td>
                <td>{{ c.creadoPorNombre || "—" }}</td>
                <td>
                  <div class="d-flex gap-1">
                    <button
                      v-if="puedeEditar(c)"
                      type="button" class="btn btn-sm btn-outline-primary" title="Editar"
                      @click="abrirEdicion(c)"
                    >
                      <i class="bi bi-pencil"></i>
                    </button>
                    <button
                      type="button" class="btn btn-sm btn-outline-secondary" title="Ver historial"
                      @click="abrirHistorial(c)"
                    >
                      <i class="bi bi-clock-history"></i>
                    </button>
                    <template v-if="!['completada', 'cancelada'].includes(c.estado)">
                      <button
                        v-if="c.estado === 'pendiente' && puedeConfirmarCompletar(c)"
                        type="button" class="btn btn-sm btn-outline-info" title="Confirmar"
                        @click="cambiarEstado(c, 'confirmada')"
                      >
                        <i class="bi bi-check-lg"></i>
                      </button>
                      <button
                        v-if="c.estado === 'confirmada' && puedeConfirmarCompletar(c)"
                        type="button" class="btn btn-sm btn-outline-success" title="Completar"
                        @click="cambiarEstado(c, 'completada')"
                      >
                        <i class="bi bi-check2-all"></i>
                      </button>
                      <button
                        v-if="puedeCancelar(c)"
                        type="button" class="btn btn-sm btn-outline-danger" title="Cancelar"
                        @click="cambiarEstado(c, 'cancelada')"
                      >
                        <i class="bi bi-x-lg"></i>
                      </button>
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <EditarCitaModal
      :visible="!!citaEnEdicion"
      :cita="citaEnEdicion"
      @close="cerrarEdicion"
      @guardado="alGuardarEdicion"
    />

    <HistorialCitaModal
      :visible="!!citaEnHistorial"
      :cita-id="citaEnHistorial?.id"
      @close="cerrarHistorial"
    />
  </div>
</template>
